import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  ArrowLeft, Trash2, Loader2,
  FileText, Image, Activity, Link2, User, CalendarClock, AlertTriangle, MessageSquare,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { STATUT_LABELS, STATUT_COLORS } from "./AtelierDashboard";
import ExcelImportDialog from "@/components/ExcelImportDialog";
import {
  useUnsavedChangesGuard,
  UnsavedChangesBanner,
  useSafeNavigate,
} from "@/components/UnsavedChangesGuard";
import IntervenantsEditor from "@/components/IntervenantsEditor";
import VehiculeCard from "@/components/VehiculeCard";
import InlineEditField from "@/components/InlineEditField";
import LignesEditor from "@/components/LignesEditor";
import OrSuiviSmsDialog from "@/components/or/OrSuiviSmsDialog";
import {
  OrHeader, OrActionsBar,
  ArticlePickerDialog, OrSmsResultDialog, OrFinaliserDialog,
  SmsPreviewDialog, StatutSmsConfirmDialog,
  OrPhotosTab, OrHistoriqueTab, OrDevisLiesTab,
  ImportDevisDialog, OrPlanifierRdvDialog,
  OrReferencesBlock, OrAssuranceCard,
} from "@/components/or";
import RevertStatusButton from "@/components/or/RevertStatusButton";
import PrincipalSelectDialog from "@/components/or/PrincipalSelectDialog";
import OrCompletudeDialog from "@/components/or/OrCompletudeDialog";
import OrAttachmentsBlock from "@/components/or/OrAttachmentsBlock";
import OrPromosBanner from "@/components/or/OrPromosBanner";
import SuiviShareDialog from "@/components/SuiviShareDialog";
import OrMessagesClientBlock from "@/components/or/OrMessagesClientBlock";
import GardiennageBlock from "@/components/GardiennageBlock";
import ChangeOrClientDialog from "@/components/or/ChangeOrClientDialog";
import OrPhotoLibraryDialog from "@/components/or/OrPhotoLibraryDialog";
import EmailComposeModal from "@/components/mail/EmailComposeModal";
import useOrActions from "@/hooks/useOrActions";
import { buildLigneFromArticle } from "@/lib/articleToLigne";

const STATUT_FLOW = [
  'cree', 'pris_en_charge', 'controle', 'devis_envoye',
  'approbation', 'commencement', 'travaux_effectue', 'pret', 'facture', 'paye',
];

// Saut direct autorisé : on peut sauter à tout statut ultérieur depuis 'cree' minimum
// (sauf facture/paye qui sont réservés aux rôles admin/comptoir et passent par la finalisation).
const SKIPPABLE_FROM = ['cree', 'pris_en_charge', 'controle', 'devis_envoye', 'approbation', 'commencement', 'travaux_effectue'];

export default function OrDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const safeNavigate = useSafeNavigate();
  // `or` = état serveur (référence pour le diff). `draft` = état local édité.
  const [or, setOr] = useState(null);
  const [draft, setDraft] = useState(null);  // {...or, lignes: [...]}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [historique, setHistorique] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingLigneIdx, setEditingLigneIdx] = useState(null);  // iter127 — index dans draft.lignes (null = ajout)
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [importDevisOpen, setImportDevisOpen] = useState(false);
  const [changeClientOpen, setChangeClientOpen] = useState(false);
  const [orTab, setOrTab] = useState("devis");  // iter170
  const [emailModalOpen, setEmailModalOpen] = useState(false);  // iter200
  const [shareOpen, setShareOpen] = useState(false);  // iter238 — lien de suivi public
  // iter266 — Pastille rouge : messages client non lus sur ce dossier
  const [msgNonLus, setMsgNonLus] = useState(0);
  // iter267 — Sélection de photos depuis la bibliothèque partagée
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [smsLinkOpen, setSmsLinkOpen] = useState(false);  // iter262 — lien ponctuel par SMS
  const [planifierOpen, setPlanifierOpen] = useState(false);  // iter225

  const [tauxHoraires, setTauxHoraires] = useState({});
  const [mecaniciens, setMecaniciens] = useState([]);

  // Comparaison superficielle d'un tableau d'intervenants
  const intervenantsEqual = (a, b) => {
    const A = a || []; const B = b || [];
    if (A.length !== B.length) return false;
    for (let i = 0; i < A.length; i++) {
      if ((A[i].mecanicien_id || "") !== (B[i].mecanicien_id || "")) return false;
      if (Math.abs((parseFloat(A[i].heures) || 0) - (parseFloat(B[i].heures) || 0)) > 0.001) return false;
    }
    return true;
  };

  // isDirty calculé : compare draft vs or (champs édités + lignes)
  // iter127 — détecte les nouvelles lignes via `_localId` (LignesEditor partagé) OU `id` préfixé `_`
  const isNewLigne = (d) => !d.id || String(d.id).startsWith("_") || !!d._localId;
  const isDirty = useMemo(() => {
    if (!or || !draft) return false;
    // Champs OR top-level
    const fields = ["type_client_tarif", "km_depose", "notes_client", "notes_internes",
                    "rabais_global_pct", "rabais_global_chf",
                    "entete_libre", "pied_libre", "intervenant_principal_id"];
    for (const k of fields) {
      if ((or[k] ?? "") !== (draft[k] ?? "")) return true;
    }
    if (!intervenantsEqual(or.intervenants, draft.intervenants)) return true;
    // Lignes (longueur ou diff)
    const ol = or.lignes || [];
    const dl = draft.lignes || [];
    if (ol.length !== dl.length) return true;
    for (let i = 0; i < dl.length; i++) {
      const d = dl[i], s = ol[i];
      if (!s) return true;
      // Nouvelle ligne au même index → dirty
      if (isNewLigne(d)) return true;
      if (d.id !== s.id) return true;
      const lineFields = ["libelle", "quantite", "prix_unitaire_ht", "tva_pct",
                          "rabais_pct", "rabais_chf", "unite", "type_ligne",
                          "categorie_compta", "categorie_mo"];
      for (const k of lineFields) {
        if ((d[k] ?? "") !== (s[k] ?? "")) return true;
      }
    }
    return false;
  }, [or, draft]);

  const dirtyCount = useMemo(() => {
    if (!or || !draft) return 0;
    let n = 0;
    const fields = ["type_client_tarif", "km_depose", "notes_client", "notes_internes",
                    "rabais_global_pct", "rabais_global_chf",
                    "entete_libre", "pied_libre", "intervenant_principal_id"];
    for (const k of fields) {
      if ((or[k] ?? "") !== (draft[k] ?? "")) n++;
    }
    if (!intervenantsEqual(or.intervenants, draft.intervenants)) n++;
    const ol = or.lignes || [];
    const dl = draft.lignes || [];
    // iter127 — Lignes ajoutées via LignesEditor (pas d'id serveur ou id préfixé _)
    n += dl.filter(isNewLigne).length;
    // Lignes supprimées
    const draftIds = new Set(dl.filter((d) => !isNewLigne(d)).map((d) => d.id));
    n += ol.filter((s) => !draftIds.has(s.id)).length;
    // Lignes modifiées (uniquement celles avec id serveur)
    for (const d of dl) {
      if (isNewLigne(d)) continue;  // déjà comptée
      const s = ol.find((x) => x.id === d.id);
      if (!s) continue;
      const lineFields = ["libelle", "quantite", "prix_unitaire_ht", "tva_pct",
                          "rabais_pct", "rabais_chf", "unite", "type_ligne",
                          "categorie_compta", "categorie_mo"];
      if (lineFields.some((k) => (d[k] ?? "") !== (s[k] ?? ""))) n++;
    }
    return n;
  }, [or, draft]);

  const fetchOr = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, m] = await Promise.all([
        api.get(`/or/${id}`),
        api.get(`/or/${id}/historique`),
        api.get("/atelier/mecaniciens").catch(() => ({ data: [] })),
      ]);
      // iter349 — les anciennes prises en charge stockaient la remarque client
      // dans un champ `notes` jamais affiché : on la remonte dans la demande client.
      const orData = { ...a.data };
      if (!orData.notes_client && orData.notes) orData.notes_client = orData.notes;
      setOr(orData);
      setDraft(JSON.parse(JSON.stringify(orData)));  // deep copy
      setHistorique(b.data);
      setMecaniciens(m.data || []);
    } catch (e) {
      toast.error("OR introuvable");
      navigate("/atelier");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchOr(); }, [fetchOr]);

  // iter266 — Non-lus du dossier, lus au montage (avant que le fil ne les marque lus)
  useEffect(() => {
    if (!id) return;
    api.get("/suivi/messages/non-lus")
      .then(({ data }) => setMsgNonLus((data?.par_or || {})[id] || 0))
      .catch(() => setMsgNonLus(0));
  }, [id]);

  // Load taux horaires for OR's type_client_tarif (utilise draft pour rafraîchir en live)
  useEffect(() => {
    if (!draft) return;
    api.get("/taux-horaires").then(({ data }) => {
      const map = {};
      data.filter((t) => t.type_tarif === draft.type_client_tarif).forEach((t) => {
        map[t.categorie] = t.taux_ht;
      });
      setTauxHoraires(map);
    });
  }, [draft?.type_client_tarif]);

  // iter127 — Handler du picker article (idx-based)
  // iter165 — Délègue à buildLigneFromArticle pour supporter Article libre + MO forfait
  const onArticlePicked = (article) => {
    const presetLigne = buildLigneFromArticle(article, { tvaField: "tva_pct", tvaDefaut: 8.1 });
    setDraft((d) => {
      const lignes = d.lignes || [];
      if (editingLigneIdx === null) {
        // Ajout d'une nouvelle ligne article
        const _localId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        return {
          ...d,
          lignes: [...lignes, {
            _localId,
            ...presetLigne,
          }],
        };
      }
      // Remplacement d'une ligne existante (icône loupe)
      return {
        ...d,
        lignes: lignes.map((l, i) => i === editingLigneIdx ? { ...l, ...presetLigne } : l),
      };
    });
  };

  // === ENREGISTRER : sync serveur via diff ===
  const saveAll = useCallback(async () => {
    if (!or || !draft) return true;
    setSaving(true);
    try {
      // 1. Update OR top-level si champs modifiés
      const orFields = ["type_client_tarif", "km_depose", "notes_client", "notes_internes",
                        "rabais_global_pct", "rabais_global_chf",
                        "entete_libre", "pied_libre", "intervenant_principal_id"];
      const orChanged = orFields.some((k) => (or[k] ?? "") !== (draft[k] ?? ""))
        || !intervenantsEqual(or.intervenants, draft.intervenants);
      if (orChanged) {
        await api.put(`/or/${id}`, {
          client_id: draft.client_id,
          vehicule_id: draft.vehicule_id,
          rdv_id: draft.rdv_id,
          type_client_tarif: draft.type_client_tarif,
          km_depose: draft.km_depose,
          notes_client: draft.notes_client,
          notes_internes: draft.notes_internes,
          photos_depose: draft.photos_depose,
          rabais_global_pct: draft.rabais_global_pct,
          rabais_global_chf: draft.rabais_global_chf,
          entete_libre: draft.entete_libre || null,
          pied_libre: draft.pied_libre || null,
          intervenants: (draft.intervenants || []).filter((x) => x.mecanicien_id && (parseFloat(x.heures) || 0) > 0),
          intervenant_principal_id: draft.intervenant_principal_id || null,
        });
      }
      // 2. Suppression lignes
      const draftIds = new Set((draft.lignes || []).filter((d) => !isNewLigne(d)).map((d) => d.id));
      for (const s of (or.lignes || [])) {
        if (!draftIds.has(s.id)) {
          await api.delete(`/or/lignes/${s.id}`);
        }
      }
      // 3. Création / mise à jour lignes (avec position issue de l'ordre du tableau)
      const draftLignes = draft.lignes || [];
      for (let pos = 0; pos < draftLignes.length; pos++) {
        const d = draftLignes[pos];
        const isNew = isNewLigne(d);
        const payload = {
          type_ligne: d.type_ligne,
          libelle: d.libelle,
          quantite: d.quantite,
          prix_unitaire_ht: d.prix_unitaire_ht,
          tva_pct: d.tva_pct ?? d.tva_taux ?? 8.1,
          rabais_pct: d.rabais_pct,
          rabais_chf: d.rabais_chf,
          unite: d.unite,
          article_id: d.article_id || null,
          categorie_compta: d.categorie_compta || null,
          categorie_mo: d.categorie_mo || null,
          position: pos + 1,
        };
        if (isNew) {
          await api.post(`/or/${id}/lignes`, payload);
        } else {
          const s = (or.lignes || []).find((x) => x.id === d.id);
          if (s) {
            const lineFields = ["libelle", "quantite", "prix_unitaire_ht", "tva_pct",
                                "rabais_pct", "rabais_chf", "unite", "type_ligne",
                                "categorie_compta", "categorie_mo"];
            const lineChanged = lineFields.some((k) => (d[k] ?? "") !== (s[k] ?? ""));
            const posChanged = (s.position ?? 0) !== pos + 1;
            if (lineChanged || posChanged) {
              await api.put(`/or/lignes/${d.id}`, payload);
            }
          }
        }
      }
      // Recharge l'état serveur (qui devient nouveau "or") + reset draft
      await fetchOr();
      toast.success("Modifications enregistrées");
      setSaving(false);
      return true;
    } catch (e) {
      setSaving(false);
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
      return false;
    }
  }, [or, draft, id, fetchOr]);

  // === ANNULER : revert au dernier état serveur ===
  const cancelAll = useCallback(() => {
    if (!isDirty) return;
    if (!window.confirm(`Annuler ${dirtyCount} modification(s) non enregistrée(s) ?`)) return;
    setDraft(JSON.parse(JSON.stringify(or)));
    toast.info("Modifications annulées");
  }, [isDirty, dirtyCount, or]);

  // === Garde anti-perte de modifs (sidebar + beforeunload) ===
  useUnsavedChangesGuard(isDirty, saveAll);

  // ============================================================
  // Hook métier : centralise toutes les actions HTTP + dialogs liés à un OR
  // (changeStatut, finaliserOr, emettreFactureMode, archive, photos, SMS, PDF…)
  // Voir /app/frontend/src/hooks/useOrActions.js
  // ============================================================
  const actions = useOrActions({
    id, or, fetchOr, isDirty, saveAll, statutLabels: STATUT_LABELS,
  });
  const {
    changeStatut,
    confirmStatutDialog, cancelConfirmStatut, confirmStatutWithoutSms, confirmStatutWithSms,
    principalDialog, cancelPrincipalDialog, confirmPrincipalAndStatut,
    completudeDialog, cancelCompletudeDialog, confirmCompletude,
    smsPreview, cancelSmsPreview, handleSmsPreviewSend, updateSmsMessage,
    finalDialog, setFinalDialog, finalSubmitting, chooseFinaliser,
    archiveOr, desarchiveOr,
    uploadPhotos, removePhoto,
    removeOr,
    sendingSms, smsResult, showSmsDialog, setShowSmsDialog, sendDevisSms, copyLink, copyDevisPublicLink,
    openPdfInTab, downloadPdf, openFacturePdfInTab, downloadFacturePdf,
  } = actions;

  // iter258 — enlèvement du véhicule par le client (visible dans le suivi public)
  const markEnleve = async (enleve) => {
    try {
      await api.post(`/or/${id}/enleve`, { enleve });
      toast.success(enleve ? "Véhicule marqué comme enlevé" : "Enlèvement annulé");
      fetchOr();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    }
  };


  if (loading || !or) return <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  // Workflow : l'atelier ne facture pas ni n'encaisse. Les transitions vers
  // `facture` et `paye` passent obligatoirement par les endpoints dédiés (/finaliser,
  // /emettre-facture, encaissement) qui créent une facture via facture_service.
  const role = user?.role;
  const canBillRoles = role === "admin" || role === "comptoir" || role === "compta";
  const TRANSITIONS_MAP = {
    // iter225 — statut 'controle' supprimé du workflow
    cree: ['devis_envoye', 'commencement', 'annule'],
    pris_en_charge: ['devis_envoye', 'commencement', 'annule'],
    controle: ['devis_envoye', 'commencement', 'annule'],  // legacy
    devis_envoye: ['approbation', 'annule'],
    approbation: ['commencement', 'annule'],
    commencement: ['travaux_effectue'],
    travaux_effectue: [],  // remplacé par dialog de finalisation
    pret: [],  // l'émission de facture se fait via le dialog "Émettre la facture" ; encaissement final ailleurs
    facture: [],  // statut LEGACY — pas de transition manuelle, géré par encaissement
    paye: [],
    annule: [],
  };
  const nextStatuts = TRANSITIONS_MAP[or.statut] || [];

  // Statuts atteignables par saut direct (workflow assoupli)
  const skippableTargets = (() => {
    if (!SKIPPABLE_FROM.includes(or.statut)) return [];
    const i = STATUT_FLOW.indexOf(or.statut);
    return STATUT_FLOW
      .slice(i + 1)
      .filter((s) => s !== "facture" && s !== "paye")  // ces 2 passent par la finalisation
      .filter((s) => s !== "pris_en_charge")  // workflow simplifié — statut legacy
      .filter((s) => s !== "controle")  // iter225 — statut supprimé du workflow
      .filter((s) => s !== "pret")  // 'pret' atteint via Finaliser, pas via saut manuel
      .filter((s) => !nextStatuts.includes(s));
  })();
  // Totaux : si draft différent du serveur, on calcule en local ; sinon on prend du serveur
  const computeTotauxFromDraft = (d) => {
    let sousTotalHt = 0, rabaisLignes = 0;
    for (const l of (d?.lignes || [])) {
      if ((l.type_ligne || "libre") === "texte") continue;
      const brut = (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire_ht) || 0);
      const rab = brut * ((parseFloat(l.rabais_pct) || 0) / 100) + (parseFloat(l.rabais_chf) || 0);
      sousTotalHt += brut;
      rabaisLignes += Math.min(rab, brut);
    }
    const htApresLignes = Math.max(0, sousTotalHt - rabaisLignes);
    const rg = htApresLignes * ((parseFloat(d?.rabais_global_pct) || 0) / 100) + (parseFloat(d?.rabais_global_chf) || 0);
    const rabaisGlobal = Math.min(rg, htApresLignes);
    const totalHt = Math.max(0, htApresLignes - rabaisGlobal);
    const ratio = htApresLignes > 0 ? totalHt / htApresLignes : 0;
    let tva = 0;
    for (const l of (d?.lignes || [])) {
      if ((l.type_ligne || "libre") === "texte") continue;
      const brut = (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire_ht) || 0);
      const rab = Math.min(brut * ((parseFloat(l.rabais_pct) || 0) / 100) + (parseFloat(l.rabais_chf) || 0), brut);
      const netLigne = (brut - rab) * ratio;
      tva += netLigne * ((parseFloat(l.tva_pct) || 0) / 100);
    }
    return {
      sous_total_ht: sousTotalHt,
      rabais_lignes_total: rabaisLignes,
      rabais_global: rabaisGlobal,
      total_ht: totalHt,
      tva: tva,
      total_ttc: totalHt + tva,
    };
  };
  const t = isDirty ? computeTotauxFromDraft(draft) : or?.totaux;
  // Le devis peut être envoyé par SMS dès qu'un OR a au moins une ligne,
  // tant qu'il n'est pas annulé / facturé / payé.
  // iter225 — Reste possible après acceptation (devis modifié = nouvelle révision,
  // l'OR ne recule pas dans le workflow).
  const canSendDevis = ['cree', 'pris_en_charge', 'controle', 'devis_envoye',
    'approbation', 'commencement', 'travaux_effectue'].includes(or.statut)
    && (draft?.lignes || []).length > 0 && role !== 'compta';
  // iter225 — Planification agenda : utile tant que les travaux n'ont pas été clôturés
  const canPlanifier = ['cree', 'pris_en_charge', 'controle', 'devis_envoye',
    'approbation', 'commencement'].includes(or.statut) && role !== 'compta';
  // L'aperçu PDF est toujours possible si lignes présentes
  const canPreviewPdf = (draft?.lignes || []).length > 0;
  // iter245 — Services en ligne : activés client par client (fiche client → Suivi en ligne)
  const suiviActif = !!or.client_detail?.suivi_actif;
  const messagerieActive = !!or.client_detail?.suivi_messagerie_actif;
  // Facture disponible dès que statut = facture/paye, ou pret avec facture émise (iter92)
  const canShowFacture = (or.statut === 'facture' || or.statut === 'paye' || (or.statut === 'pret' && !!or.facture_id))
    && canBillRoles && (draft?.lignes || []).length > 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="or-detail-page">
      <OrPromosBanner
        orId={or.id}
        canEdit={["admin", "comptoir"].includes(user?.role)}
        onChange={fetchOr}
      />
      {/* iter293/iter310 — publication sur le lien de suivi. N'a de sens que si le client
          dispose réellement d'un lien (service « Suivi en ligne » activé sur sa fiche). */}
      {["admin", "comptoir"].includes(user?.role) && suiviActif && (
        <div
          className={`mb-4 rounded-lg border px-3 py-2 flex items-center justify-between gap-3 flex-wrap ${
            or.suivi_publie === false
              ? "bg-amber-50 border-amber-200"
              : "bg-gray-50 border-gray-200"
          }`}
          data-testid="or-suivi-publication"
        >
          <span className="text-xs text-gray-700">
            {or.suivi_publie === false
              ? "Masqué du lien de suivi — le client ne voit rien et ne reçoit aucune notification."
              : "Publié sur le lien de suivi du client."}
          </span>
          <Button
            size="sm"
            variant={or.suivi_publie === false ? "default" : "outline"}
            className={or.suivi_publie === false ? "bg-[#52a675] hover:bg-[#3d8a5c] h-7 text-xs" : "h-7 text-xs"}
            onClick={async () => {
              try {
                await api.post(`/or/${or.id}/suivi-publication`, { publie: or.suivi_publie === false });
                toast.success(or.suivi_publie === false ? "Dossier publié sur le lien" : "Dossier masqué du lien");
                fetchOr();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Action impossible");
              }
            }}
            data-testid="or-suivi-publication-toggle"
          >
            {or.suivi_publie === false ? "Publier sur le lien" : "Masquer du lien"}
          </Button>
        </div>
      )}
      {isDirty && (
        <UnsavedChangesBanner
          count={dirtyCount}
          onSave={saveAll}
          onCancel={cancelAll}
          saving={saving}
        />
      )}

      <button onClick={() => safeNavigate("/atelier")} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3 mt-2" data-testid="or-back">
        <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
      </button>

      <Card className="p-3.5 sm:p-4 border-gray-200 rounded-xl shadow-sm mb-4 overflow-hidden relative">
        {/* iter254 — liseré de statut : repère visuel immédiat, coût zéro en hauteur */}
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#52a675] via-[#52a675]/40 to-transparent" />
        <OrHeader
          or={or}
          setOr={setOr}
          draft={draft}
          setDraft={setDraft}
          totaux={t}
          statutLabels={STATUT_LABELS}
          statutColors={STATUT_COLORS}
          navigate={navigate}
          fetchOr={fetchOr}
          onChangeClient={() => setChangeClientOpen(true)}
        />

        {/* Détails véhicule — pleine largeur sous le header pour une lisibilité optimale (mobile & desktop) */}
        {or.vehicule_detail && (
          <div className="mt-2.5">
            <VehiculeCard
              vehicule={or.vehicule_detail}
              onUpdate={fetchOr}
              extraStat={(
                /* iter247 — km à la dépose : propre à l'OR, affiché avec le km véhicule */
                <div className="flex-shrink-0 min-w-0 text-left sm:text-right" data-testid="or-km-depose-stat">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    KM
                  </div>
                  <div className="font-mono text-sm font-semibold text-gray-900">
                    <InlineEditField
                      value={or.km_depose}
                      onSave={async (v) => {
                        const km = v ? parseInt(v, 10) : null;
                        const { data } = await api.patch(`/or/${or.id}`, { km_depose: km });
                        setOr(data);
                        setDraft((d) => (d ? { ...d, km_depose: km } : d));
                      }}
                      type="number"
                      inputMode="numeric"
                      placeholder="—"
                      formatDisplay={(v) =>
                        v ? `${v.toLocaleString("fr-CH")} km` : (
                          <span className="text-amber-600 italic text-xs">ajouter</span>
                        )
                      }
                      inputClassName="w-20 text-xs"
                      testId="or-km-inline"
                    />
                  </div>
                </div>
              )}
            />
          </div>
        )}

        {/* iter235 — Gardiennage pneus du véhicule (info discrète + alerte à facturer) */}
        {or.vehicule_id && <GardiennageBlock vehiculeId={or.vehicule_id} compact />}

        {/* iter233 — Assurance du dossier (dès l'ouverture de l'OR) + en-têtes / références */}
        {(draft?.type_client_tarif === "assurance" || or.assurance_id) && (
          <OrAssuranceCard
            or={or}
            onUpdated={(data) => {
              setOr(data);
              setDraft((d) => (d ? { ...d, assurance_id: data.assurance_id, references: data.references } : d));
            }}
          />
        )}
        <OrReferencesBlock
          or={or}
          onUpdated={(data) => {
            setOr(data);
            setDraft((d) => (d ? { ...d, references: data.references, no_sinistre: data.no_sinistre } : d));
          }}
        />

        {/* Statut transitions + actions devis/facture (extrait dans OrActionsBar) */}
        <OrActionsBar
          or={or}
          nextStatuts={nextStatuts}
          skippableTargets={skippableTargets}
          canBillRoles={canBillRoles}
          canPreviewPdf={canPreviewPdf}
          canSendDevis={canSendDevis}
          canShowFacture={canShowFacture}
          sendingSms={sendingSms}
          statutLabels={STATUT_LABELS}
          changeStatut={changeStatut}
          setFinalDialog={setFinalDialog}
          archiveOr={archiveOr}
          desarchiveOr={desarchiveOr}
          openPdfInTab={openPdfInTab}
          downloadPdf={downloadPdf}
          sendDevisSms={sendDevisSms}
          copyDevisPublicLink={copyDevisPublicLink}
          openFacturePdfInTab={openFacturePdfInTab}
          downloadFacturePdf={downloadFacturePdf}
          onSendOrEmail={() => setEmailModalOpen(true)}
          markEnleve={markEnleve}
          onPlanifier={canPlanifier ? () => setPlanifierOpen(true) : null}
          planifieDate={or.rdv_planifie_date}
          planifieHeure={or.rdv_planifie_heure}
        />

        {/* iter91 — Bouton Retour étape (recule l'OR à l'étape précédente) */}
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <RevertStatusButton or={or} onRefresh={fetchOr} />
          {or.devis_complement_en_attente && (
            <span
              className="text-xs px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-800 inline-flex items-center gap-1"
              data-testid="or-complement-badge"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Complément de devis v{or.devis_revision} — réponse client en attente
              <button
                type="button"
                className="ml-1 px-1.5 py-0.5 rounded bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors"
                onClick={async () => {
                  try {
                    await api.post(`/or/${or.id}/devis/complement-confirme`);
                    toast.success("Complément validé — le client a confirmé");
                    fetchOr();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Validation impossible");
                  }
                }}
                data-testid="or-complement-confirmer"
              >
                Confirmation reçue
              </button>
            </span>
          )}
          {!or.devis_complement_en_attente && or.devis_complement_accepte_at && (
            <span
              className="text-xs px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 inline-flex items-center gap-1"
              data-testid="or-complement-accepte-badge"
            >
              ✓ Complément v{or.devis_revision} accepté par le client
            </span>
          )}
        </div>
      </Card>

      {/* iter266 — OR sans intervenant : auto-attribution en un clic */}
      {/* iter275 — Réservé aux mécanos : l'admin attribue, il ne s'approprie pas */}
      {!or.intervenant_principal_id && user?.role === "atelier" && (
        <div className="mb-4">
          <Button
            variant="outline"
            size="sm"
            className="border-[#52a675] text-[#2d5f3f] hover:bg-[#52a675]/10"
            onClick={async () => {
              try {
                await api.post(`/or/${or.id}/prendre`);
                toast.success("OR attribué — il apparaît dans « Mes OR »");
                fetchOr();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Attribution impossible");
              }
            }}
            data-testid="or-prendre-btn"
          >
            <UserCheck className="w-4 h-4 mr-2" /> Je prends cet OR
          </Button>
        </div>
      )}

      {/* iter349 — Demande du client et notes internes : saisies à la création ou à la
          prise en charge, elles n'étaient visibles nulle part sur la fiche OR. */}
      <Card className="p-4 border-gray-200 mb-4" data-testid="or-notes-card">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
              Demande du client
            </Label>
            <p className="text-[11px] text-gray-500 mb-1">Reprise sur le devis et la facture</p>
            <Textarea
              rows={3}
              value={draft.notes_client || ""}
              onChange={(e) => setDraft((d) => ({ ...d, notes_client: e.target.value }))}
              placeholder="Bruit côté droit, voyant moteur allumé…"
              data-testid="or-notes-client-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
              Notes internes
            </Label>
            <p className="text-[11px] text-gray-500 mb-1">Jamais visibles par le client</p>
            <Textarea
              rows={3}
              value={draft.notes_internes || ""}
              onChange={(e) => setDraft((d) => ({ ...d, notes_internes: e.target.value }))}
              placeholder="Pièce commandée, à rappeler avant démontage…"
              data-testid="or-notes-internes-input"
            />
          </div>
        </div>
      </Card>

      {/* iter170 — Pièces jointes PDF (rapport Auto-I, attestations, garanties…) */}
      <div className="mb-4">
        <OrAttachmentsBlock orId={or.id} canEdit={true} vehiculeId={or.vehicule_id} />
      </div>

      {/* iter240/245 — Fil de discussion : uniquement si le client a la messagerie activée */}
      {suiviActif && messagerieActive && (
        <div className="mb-4">
          {msgNonLus > 0 && (
            <div
              className="mb-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700"
              data-testid="or-messages-unread-banner"
            >
              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-red-600 text-white text-xs">
                {msgNonLus}
              </span>
              {msgNonLus > 1 ? "nouveaux messages du client" : "nouveau message du client"}
            </div>
          )}
          <OrMessagesClientBlock orId={or.id} />
        </div>
      )}

      {/* iter262 — Client sans suivi global : lien ponctuel envoyé par SMS */}
      {!suiviActif && (
        <div className="mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSmsLinkOpen(true)}
            data-testid="or-suivi-sms-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2 text-[#52a675]" /> Envoyer le lien de suivi par SMS
          </Button>
        </div>
      )}

      {/* iter238/245 — Suivi public : uniquement si le service est activé sur la fiche client */}
      {suiviActif && (
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} data-testid="or-share-suivi-btn">
          <Link2 className="w-4 h-4 mr-2" /> Partager le suivi de ce dossier
        </Button>
        <Button variant="outline" size="sm" onClick={() => setSmsLinkOpen(true)} data-testid="or-suivi-sms-btn-2">
          <MessageSquare className="w-4 h-4 mr-2 text-[#52a675]" /> Lien de ce dossier par SMS
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={or.suivi_hidden ? "border-amber-300 text-amber-700" : ""}
          onClick={async () => {
            try {
              await api.patch(`/or/${or.id}/suivi-visibilite`, { hidden: !or.suivi_hidden });
              toast.success(or.suivi_hidden ? "Dossier réaffiché sur le suivi client" : "Dossier masqué du suivi client");
              fetchOr();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Action impossible");
            }
          }}
          data-testid="or-suivi-hidden-btn"
        >
          {or.suivi_hidden ? "Masqué du suivi client — réafficher" : "Masquer ce dossier du suivi client"}
        </Button>
      </div>
      )}
      <OrSuiviSmsDialog
        open={smsLinkOpen}
        onClose={() => setSmsLinkOpen(false)}
        orId={or.id}
        orNumero={or.numero}
        plaque={or.plaque}
        telephone={or.client_detail?.tel_principal || or.client_detail?.tel_secondaire || ""}
      />
      <SuiviShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        orId={or.id}
        orNumero={or.numero}
      />

      <Tabs value={orTab} onValueChange={setOrTab}>
        {/* iter252 — mobile : les 4 onglets deviennent un menu (la bande débordait) */}
        <div className="md:hidden">
          <Select value={orTab} onValueChange={setOrTab}>
            <SelectTrigger className="h-11" data-testid="or-tab-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="devis" data-testid="or-tab-option-devis">Devis &amp; lignes</SelectItem>
              <SelectItem value="liens" data-testid="or-tab-option-liens">Devis liés</SelectItem>
              <SelectItem value="photos" data-testid="or-tab-option-photos">
                Photos ({(or.photos_depose || []).length})
              </SelectItem>
              <SelectItem value="historique" data-testid="or-tab-option-historique">Historique</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <TabsList className="hidden md:flex w-full justify-start flex-nowrap">
          <TabsTrigger value="devis" data-testid="tab-or-devis"><FileText className="w-4 h-4 mr-2" /> Devis & lignes</TabsTrigger>
          <TabsTrigger value="liens" data-testid="tab-or-liens"><Link2 className="w-4 h-4 mr-2" /> Devis liés</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-or-photos"><Image className="w-4 h-4 mr-2" /> Photos ({(or.photos_depose || []).length})</TabsTrigger>
          <TabsTrigger value="historique" data-testid="tab-or-historique"><Activity className="w-4 h-4 mr-2" /> Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="devis" className="mt-4">
          <Card className="border-gray-200 p-4">
            <LignesEditor
              lignes={draft?.lignes || []}
              setLignes={(next) => setDraft((d) => ({ ...d, lignes: next }))}
              rabaisGlobal={{ pct: draft?.rabais_global_pct || 0, chf: draft?.rabais_global_chf || 0 }}
              setRabaisGlobal={(rg) => setDraft((d) => ({ ...d, rabais_global_pct: rg.pct, rabais_global_chf: rg.chf }))}
              tvaField="tva_pct"
              tauxHoraires={tauxHoraires}
              externalArticlePicker
              onPickArticleClick={(idx) => { setEditingLigneIdx(idx); setPickerOpen(true); }}
              externalExcelImport
              onExcelImportClick={() => setExcelImportOpen(true)}
              extraToolbarButtons={
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => setImportDevisOpen(true)}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="add-ligne-import-devis"
                  title="Récupérer les lignes d'un devis déjà créé"
                >
                  <FileText className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Importer un devis</span>
                </Button>
              }
            />
          </Card>

          {/* iter127 — Bloc texte d'en-tête / pied / intervenants (déplacé hors LignesEditor partagé) */}
          <Card className="border-gray-200 p-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* iter249 — Les en-têtes se saisissent désormais uniquement dans le bloc
                  « En-tête / Références » (structuré, repris sur devis et factures).
                  Ce champ texte libre ne reste visible que pour les OR qui en ont déjà un,
                  afin de pouvoir le relire ou le vider — il n'est plus proposé ailleurs. */}
              {(draft?.entete_libre || "").trim() !== "" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">
                    Texte d&apos;en-tête (PDF) — ancien format
                  </Label>
                  <Textarea
                    value={draft?.entete_libre || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, entete_libre: e.target.value }))}
                    rows={3}
                    className="text-sm mt-1"
                    data-testid="or-entete-libre"
                  />
                  <div className="text-[10px] text-gray-400 mt-1">
                    Videz ce champ pour n&apos;utiliser que le bloc « En-tête / Références ».
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">
                  Texte de pied (PDF)
                </Label>
                <Textarea
                  value={draft?.pied_libre || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, pied_libre: e.target.value }))}
                  placeholder="Texte affiché en bas du devis / facture PDF après les totaux…"
                  rows={3}
                  className="text-sm mt-1"
                  data-testid="or-pied-libre"
                />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="mb-3">
                <label className="text-xs font-semibold text-gray-700 mb-1 block flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 bg-[#52a675] rounded-full" />
                  Intervenant principal
                </label>
                <select
                  value={draft?.intervenant_principal_id || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, intervenant_principal_id: e.target.value || null }))}
                  data-testid="or-principal-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#52a675] focus:ring-1 focus:ring-[#52a675]/40 focus:outline-none"
                >
                  <option value="">— Aucun (sera demandé au démarrage) —</option>
                  {(mecaniciens || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </div>
              <IntervenantsEditor
                intervenants={draft?.intervenants || []}
                setIntervenants={(next) => setDraft((d) => ({ ...d, intervenants: next }))}
                mecaniciens={mecaniciens}
              />
              <div className="text-[10px] text-gray-400 mt-2">
                Utilisé pour les statistiques par mécanicien (page <em>Stats atelier</em>).
                Le CA HT est réparti au prorata des heures.
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="photos" className="mt-4">
          <OrPhotosTab
            onOpenLibrary={() => setLibraryOpen(true)}
            photos={or.photos_depose || []}
            partagees={(or.photos_public?.depose) || []}
            onUpload={(files) => uploadPhotos(files, "depose")}
            onRemove={(i) => removePhoto(i, "depose")}
            onTogglePartage={async (index, share) => {
              try {
                await api.patch(`/or/${or.id}/photos/partage`, { type: "depose", index, share });
                fetchOr();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Partage impossible");
              }
            }}
          />
        </TabsContent>

        <TabsContent value="liens" className="mt-4">
          <OrDevisLiesTab orId={or.id} />
        </TabsContent>

        <TabsContent value="historique" className="mt-4">
          <OrHistoriqueTab
            historique={historique}
            statutLabels={STATUT_LABELS}
            statutColors={STATUT_COLORS}
          />
        </TabsContent>
      </Tabs>

      <div className="mt-4 flex gap-2">
        {user?.role === "admin" && (
          <Button variant="outline" onClick={removeOr} className="text-red-600 hover:bg-red-50" data-testid="or-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Supprimer l&apos;OR
          </Button>
        )}
      </div>

      <ArticlePickerDialog
        open={pickerOpen}
        onClose={() => { setPickerOpen(false); setEditingLigneIdx(null); }}
        onPick={(a) => { onArticlePicked(a); setEditingLigneIdx(null); }}
      />

      <ExcelImportDialog
        open={excelImportOpen}
        onClose={() => setExcelImportOpen(false)}
        orId={id}
        onImported={() => fetchOr()}
      />

      <ImportDevisDialog
        open={importDevisOpen}
        onClose={() => setImportDevisOpen(false)}
        orId={id}
        clientId={or?.client_id}
        vehiculeId={or?.vehicule_id}
        currentLignesCount={(draft?.lignes || []).length}
        onImported={() => fetchOr()}
      />

      {/* Dialog confirmation SMS devis (extrait dans OrSmsResultDialog) */}
      <OrSmsResultDialog
        open={showSmsDialog}
        onClose={() => setShowSmsDialog(false)}
        smsResult={smsResult}
        onCopyLink={copyLink}
      />

      {/* iter43 — Dialog de choix lors de bascule statut (devis_envoye / pret) */}
      <StatutSmsConfirmDialog
        open={confirmStatutDialog.open}
        onClose={cancelConfirmStatut}
        statutCible={confirmStatutDialog.target}
        onContinueWithoutSms={confirmStatutWithoutSms}
        onContinueWithSms={confirmStatutWithSms}
      />

      {/* iter43 — Aperçu visuel d'un SMS avant envoi (utilisé par les boutons devis SMS
          et le choix "Avec SMS" du dialog de bascule statut) */}
      <SmsPreviewDialog
        open={smsPreview.open}
        onClose={cancelSmsPreview}
        onSend={handleSmsPreviewSend}
        to={smsPreview.to}
        message={smsPreview.message}
        loading={smsPreview.loading}
        onMessageChange={updateSmsMessage}
        lienAuto={smsPreview.lienAuto}
        sending={smsPreview.sending || smsPreview.loading}
        title={
          ["devis_envoye", "__devis_sms__"].includes(smsPreview.target)
            ? "Aperçu — SMS du devis au client"
            : "Aperçu — SMS véhicule prêt"
        }
        sendLabel={
          ["devis_envoye", "__devis_sms__"].includes(smsPreview.target)
            ? "Envoyer le devis"
            : "Envoyer la notification"
        }
      />

      {/* Dialog finalisation : choix paiement immédiat ou 30j (extrait dans OrFinaliserDialog) */}
      <OrFinaliserDialog
        open={finalDialog}
        onClose={() => setFinalDialog(false)}
        or={or}
        draft={draft}
        setDraft={setDraft}
        mecaniciens={mecaniciens}
        finalSubmitting={finalSubmitting}
        onChooseFinaliser={chooseFinaliser}
      />

      {/* iter119 — Modal de sélection de l'intervenant principal au démarrage de l'OR */}
      <PrincipalSelectDialog
        open={principalDialog.open}
        mecaniciens={mecaniciens}
        targetStatut={principalDialog.target}
        onCancel={cancelPrincipalDialog}
        onConfirm={confirmPrincipalAndStatut}
      />

      {/* iter346 — Rappel de complétude au démarrage des travaux */}
      <OrCompletudeDialog
        open={completudeDialog.open}
        manquants={completudeDialog.manquants}
        onClose={cancelCompletudeDialog}
        onConfirm={confirmCompletude}
      />

      {/* iter170 — Dialog de changement de client sur OR */}
      {changeClientOpen && or && (
        <ChangeOrClientDialog
          orId={or.id}
          currentClientId={or.client_id}
          currentClientName={or.client_nom}
          onClose={() => setChangeClientOpen(false)}
          onChanged={fetchOr}
        />
      )}

      {/* iter267 — Photos issues de la bibliothèque partagée */}
      <OrPhotoLibraryDialog
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        orId={or.id}
        vehiculeId={or.vehicule_id}
        onAttached={fetchOr}
      />

      {/* iter225 — Planification agenda de l'OR */}
      <OrPlanifierRdvDialog
        open={planifierOpen}
        onClose={() => setPlanifierOpen(false)}
        or={or}
        onPlanned={fetchOr}
      />

      {/* iter200 — Modal d'envoi email métier depuis un OR */}
      <EmailComposeModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        kind="or"
        entityId={or?.id}
        entityLabel={or?.numero ? `OR ${or.numero}` : "OR"}
        onSent={fetchOr}
      />

    </div>
  );
}
